import type {
  ClientType,
  FeedExportLogDataType,
  FeedImportLogDataType,
  PaginatedResponse,
} from "types/DataTypes.ts";

import { api } from "./api.ts";

type GetClientsArgs = {
  searchTerm?: string;
  limit?: number;
  offset?: number;
};

type GetClientByIdArgs = {
  id: string | number;
};

type GetClientFeedsByIdArgs = {
  clientId: string | number;
};

type PatchClientArgs = {
  id: string | number;
  data: Partial<ClientType>;
};

type CreateClientArgs = {
  data: Partial<ClientType>;
};

type DeleteClientArgs = {
  id: string | number;
};

type ClientPricingFieldArgs = {
  id: string | number;
  pricingField: string;
};

type UploadAdjusterImportArgs = {
  id: string | number;
  file: FormData;
};

export const clientAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getClients: build.query<PaginatedResponse<ClientType>, GetClientsArgs>({
      query: ({ searchTerm, limit = 25, offset = 0 }) => ({
        url: `api/v1/clients/`,
        method: "GET",
        params: {
          ...(searchTerm ? { search: searchTerm } : {}),
          limit,
          offset,
        },
      }),
      providesTags: ["Clients"],
    }),
    getClientById: build.query<ClientType, GetClientByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/clients/${id}/`,
        method: "GET",
      }),
      providesTags: ["Client"],
    }),
    getClientFeedsById: build.query<
      PaginatedResponse<FeedImportLogDataType>,
      GetClientFeedsByIdArgs
    >({
      query: ({ clientId }) => ({
        url: `api/v1/feed_import_logs/`,
        method: "GET",
        params: {
          client_id: clientId,
        },
      }),
    }),
    getClientExportFeedsById: build.query<
      PaginatedResponse<FeedExportLogDataType>,
      GetClientFeedsByIdArgs
    >({
      query: ({ clientId }) => ({
        url: `api/v1/feed_export_logs/`,
        method: "GET",
        params: {
          client_id: clientId,
        },
      }),
    }),
    patchClientById: build.mutation<ClientType, PatchClientArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/clients/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Client", "Clients"],
    }),
    // Archiving is a dedicated action rather than a PATCH of the list so the
    // server validates and audits one fee type at a time. "Invoices" is
    // invalidated too so an open invoice's line-item type selector drops or
    // regains the fee type without a reload.
    archiveClientPricingField: build.mutation<
      ClientType,
      ClientPricingFieldArgs
    >({
      query: ({ id, pricingField }) => ({
        url: `api/v1/clients/${id}/archive-pricing-field/`,
        method: "POST",
        body: { pricing_field: pricingField },
      }),
      invalidatesTags: ["Client", "Clients", "Invoices"],
    }),
    restoreClientPricingField: build.mutation<
      ClientType,
      ClientPricingFieldArgs
    >({
      query: ({ id, pricingField }) => ({
        url: `api/v1/clients/${id}/restore-pricing-field/`,
        method: "POST",
        body: { pricing_field: pricingField },
      }),
      invalidatesTags: ["Client", "Clients", "Invoices"],
    }),
    createClient: build.mutation<ClientType, CreateClientArgs>({
      query: ({ data }) => ({
        url: `api/v1/clients/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Client", "Clients"],
    }),
    deleteClient: build.mutation<void, DeleteClientArgs>({
      query: ({ id }) => ({
        url: `api/v1/clients/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Clients"],
    }),
    uploadAdjusterImportForClient: build.mutation<
      unknown,
      UploadAdjusterImportArgs
    >({
      query: ({ id, file }) => ({
        url: `api/v1/clients/${id}/adjuster_import/`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["Client"],
    }),
    bulkRREImportForClient: build.mutation({
      query: ({ accessToken, id, file }) => ({
        url: `api/v1/clients/${id}/bulk_rre_import/`,
        method: "POST",
        body: file,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      invalidatesTags: ["Client", "Clients"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetClientsQuery,
  useGetClientByIdQuery,
  usePatchClientByIdMutation,
  useArchiveClientPricingFieldMutation,
  useRestoreClientPricingFieldMutation,
  useCreateClientMutation,
  useDeleteClientMutation,
  useUploadAdjusterImportForClientMutation,
  useGetClientFeedsByIdQuery,
  useGetClientExportFeedsByIdQuery,
  useBulkRREImportForClientMutation,
} = clientAPI;
